import classNames from 'classnames';
import React from 'react';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import { IconType } from 'components/Icon/constants';
import { Title } from 'components/Toolbar/constants';
import Control from 'components/Toolbar/Controls/Control';
import { ControlState } from 'components/Toolbar/Controls/Control/constants';
import HeadingTagDropdown from 'components/Toolbar/Dropdowns/HeadingTag';
import { useControlWithDropdown, useControlWithTooltip } from 'components/Toolbar/hooks';
import { HeadingTagType } from 'const';
import * as Models from 'models';
import { intlGet } from 'utils/intlGet';
import styles from './styles.module.scss';

type Props = {
  currentHeadingTag: HeadingTagType | undefined;
  toggleHeadingTag(headingTag: HeadingTagType): void;
  controlState?: Models.ControlState;
  dataTestId: string;
  hasBrandStyleHeadingTag: boolean;
};

const EMPTY_VALUE = '-';
const NOT_AVAILABLE = 'N/A';

const HeadingTagControl: React.FunctionComponent<Props> = (props) => {
  const { toggleHeadingTag, controlState = ControlState, dataTestId, currentHeadingTag, hasBrandStyleHeadingTag } = props;
  const { control, isOpen, state, toggleDropdown } = useControlWithDropdown({ controlState });
  const { hoverOnControl, hoverOnDropdown, onControlEnter, onDropdownEnter, onControlLeave, onDropdownLeave } = useControlWithTooltip();

  const headingTagControlValue = !hasBrandStyleHeadingTag ? NOT_AVAILABLE : (currentHeadingTag?.toUpperCase() ?? EMPTY_VALUE);

  return (
    <AccessibleDiv
      onMouseEnter={onControlEnter}
      onMouseLeave={onControlLeave}
      className={classNames(styles.ControlWrapper, { [styles.ControlWrapperTooltip]: hoverOnControl && !hoverOnDropdown })}
      data-tooltip={intlGet('EditorToolbar.Tooltip', Title.HEADING_TAG)}
      data-testid={dataTestId}
    >
      <div className={
        classNames(
          styles.HeadingTagControlValue,
          { [styles.disabled]: !hasBrandStyleHeadingTag })
      }>
        {headingTagControlValue}
      </div>
      <Control
        disabled={!hasBrandStyleHeadingTag}
        className={styles.HeadingTag}
        controlState={controlState}
        preventBlur={false}
        ref={control}
        onToggle={toggleDropdown}
        state={state}
        title={Title.HEADING_TAG}
        type={IconType.CHEVRON_DOWN}
        tooltipEnabled={false}
        dataTestId={`${dataTestId}-control`}
      >
        {
          isOpen &&
          <HeadingTagDropdown
            currentHeadingTag={currentHeadingTag}
            toggleHeadingTag={toggleHeadingTag}
            onMouseEnter={onDropdownEnter}
            onMouseLeave={onDropdownLeave}
            onCloseDropdown={toggleDropdown}
            dataTestId={`${dataTestId}-dropdown`}
          />
        }
      </Control>
    </AccessibleDiv>
  );
};

export default HeadingTagControl;
